'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { toArray } from '@/lib/utils/array'
import { Icon } from '@/ui/icons/icon/Icon'
import { Tag } from '@/ui/tag/Tag.client'
import { useState } from 'react'
import { TagGroup as AriaTagGroup, Label, TagList, type Selection } from 'react-aria-components'

type FilterValue = string

type FilterFields = {
  label: string
  values: {
    label: string
    value: FilterValue
  }[]
}

type InputGroupProps = {
  /** The filters to display */
  filter: FilterFields

  /** Determines whether several values can be selected at once */
  multiple?: boolean

  /** Hide the filter group title */
  hideTitle?: boolean

  /** Handler when an option is selected or deselected */
  onSelectionChange?: (label: string, filter: FilterValue | FilterValue[]) => void

  /** The selected values */
  selectedValues?: FilterValue[] | FilterValue | null

  /** The default selected values */
  defaultSelectedValues?: FilterValue[] | FilterValue | null

  /** The values to show as disabled */
  disabledValues?: FilterValue[]

  /** Handler when an option is selected or deselected */
  onInputChange?: (isSelected: boolean, value: FilterValue) => void

  /** Text to show in the notification */
  notificationNumber?: number

  /** Additional classes to add to the component */
  className?: string
}

export function FilterGroup({
  filter,
  multiple = false,
  hideTitle = false,
  selectedValues,
  defaultSelectedValues,
  disabledValues = [],
  onSelectionChange,
  onInputChange,
  className,
}: InputGroupProps) {
  // Defer rendering until mount to avoid hydration mismatches (ex: tabIndex) with React Aria Components.
  const isMounted = useNextIsClientHydrated()

  const isControlled = selectedValues !== undefined
  const [uncontrolledSelection, setUncontrolledSelection] = useState<Set<FilterValue>>(
    () => new Set(toArray(defaultSelectedValues)),
  )
  const currentSelection = isControlled ? new Set(toArray(selectedValues)) : uncontrolledSelection

  const disabledKeys = filter.values
    .filter(({ label }) => disabledValues.includes(label))
    .map(({ value }) => value)

  if (!isMounted) return null

  const handleSelectionChange = (keys: Selection) => {
    const newSelection =
      keys === 'all' ? filter.values.map(({ value }) => value) : Array.from(keys, String)
    const newSelectionSet = new Set(newSelection)

    if (onInputChange) {
      const added = newSelection.find((value) => !currentSelection.has(value))
      const removed = [...currentSelection].find((value) => !newSelectionSet.has(value))

      if (added !== undefined) {
        onInputChange(true, added)
      } else if (removed !== undefined) {
        onInputChange(false, removed)
      }
    }

    if (!isControlled) {
      setUncontrolledSelection(newSelectionSet)
    }

    onSelectionChange?.(filter.label, multiple ? newSelection : (newSelection[0] ?? ''))
  }

  return (
    <div className={twMerge('mt-2 gap-2', className, 'flex flex-col')}>
      <AriaTagGroup
        className="flex flex-col gap-1"
        aria-label={hideTitle ? filter.label : undefined}
        selectionMode={multiple ? 'multiple' : 'single'}
        disallowEmptySelection={!multiple}
        selectedKeys={isControlled ? currentSelection : undefined}
        defaultSelectedKeys={isControlled ? undefined : currentSelection}
        disabledKeys={disabledKeys}
        onSelectionChange={handleSelectionChange}
      >
        {!hideTitle && (
          <Label className="font-bold uppercase text-brand-2-900">{filter.label}</Label>
        )}
        <TagList data-size="md" className="flex flex-wrap gap-1">
          {filter.values?.map(({ label, value }) => {
            const isSelected = currentSelection.has(value)

            return (
              <Tag
                key={value}
                id={value}
                aria-label={`${filter.label}-${label}`}
                data-testid="filter-group/element"
                className={twMerge('bg-white', !isSelected && 'data-[disabled]:bg-white')}
              >
                <span className="inline-flex items-center gap-0.5">
                  {label}
                  {multiple && isSelected && <Icon.X size="sm" className="shrink-0" />}
                </span>
              </Tag>
            )
          })}
        </TagList>
      </AriaTagGroup>
    </div>
  )
}
